/**
 * Company Settings Page
 * Lets company administrators configure payroll policies:
 * grace period and timezone.
 */

import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updatePayrollSettings } from '../services/company.service';
import { PageHeader } from '../components/common';
import { COLORS, SPACING, TYPOGRAPHY, BORDER_RADIUS, SHADOWS } from '../constants/theme';
import { LoadingState } from '../components/dashboard';
import { SELECT_CHEVRON_STYLE } from '../styles/components';

export function CompanySettings() {
  const { currentUser, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Form state — seeded from already-fetched currentUser (useAuth fetches company on sign-in)
  const [gracePeriodMinutes, setGracePeriodMinutes] = useState(currentUser?.gracePeriodMinutes ?? 5);
  const [timezone, setTimezone] = useState(currentUser?.companyTimezone ?? 'Europe/Dublin');

  const handleSave = async () => {
    if (!currentUser?.companyId) return;
    setSuccessMessage('');
    setErrorMessage('');

    setSaving(true);
    try {
      await updatePayrollSettings(currentUser.companyId, {
        gracePeriodMinutes,
        timezone,
      });
      await refreshUser();
      setSuccessMessage('Settings saved successfully.');
    } catch (err: unknown) {
      console.error('Error saving payroll settings:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) return <LoadingState message="Loading settings..." />;

  return (
    <div style={{ padding: SPACING['2xl'], maxWidth: '680px' }}>
      <PageHeader title="Company Settings" subtitle="Configure payroll policies that apply to all staff across your company." />

      {/* Grace Period */}
      <SettingsCard title="Attendance Grace Period">
        <p style={descStyle}>
          Minutes of tolerance before a late clock-in or early clock-out is marked for review.
          Industry standard is 5–10 minutes.
        </p>
        <div style={rowStyle}>
          <label style={labelStyle}>Grace Period (minutes)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
            <input
              type="number"
              min={0}
              max={60}
              value={gracePeriodMinutes}
              onChange={(e) => setGracePeriodMinutes(Math.max(0, Math.min(60, Number(e.target.value))))}
              style={numberInputStyle}
            />
            <span style={{ fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary }}>min (0–60)</span>
          </div>
        </div>
      </SettingsCard>

      {/* Timezone */}
      <SettingsCard title="Company Timezone">
        <p style={descStyle}>
          The timezone used when scheduling shifts. Shift times (e.g. "09:00–17:00") are interpreted
          in this timezone when generating jobs. Set this to the timezone where your company operates.
        </p>
        <div style={rowStyle}>
          <label style={labelStyle}>Timezone</label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            style={selectStyle}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
      </SettingsCard>

      {/* Feedback */}
      {successMessage && (
        <div style={{ ...feedbackStyle, backgroundColor: COLORS.success.light, color: COLORS.success.dark }}>
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div style={{ ...feedbackStyle, backgroundColor: COLORS.danger.light, color: COLORS.danger.dark }}>
          {errorMessage}
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          marginTop: SPACING.xl,
          padding: `${SPACING.md} ${SPACING['2xl']}`,
          backgroundColor: saving ? COLORS.gray[400] : COLORS.primary.main,
          color: '#ffffff',
          border: 'none',
          borderRadius: BORDER_RADIUS.md,
          fontSize: TYPOGRAPHY.fontSize.base,
          fontWeight: TYPOGRAPHY.fontWeight.semibold,
          cursor: saving ? 'not-allowed' : 'pointer',
          boxShadow: SHADOWS.sm,
        }}
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SettingsCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      backgroundColor: COLORS.background.primary,
      border: `1px solid ${COLORS.border.light}`,
      borderRadius: BORDER_RADIUS.xl,
      padding: SPACING.xl,
      marginBottom: SPACING.xl,
      boxShadow: SHADOWS.sm,
    }}>
      <h2 style={{ fontSize: TYPOGRAPHY.fontSize.lg, fontWeight: TYPOGRAPHY.fontWeight.semibold, color: COLORS.text.primary, marginBottom: SPACING.lg }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const descStyle: React.CSSProperties = {
  fontSize: TYPOGRAPHY.fontSize.sm,
  color: COLORS.text.secondary,
  marginBottom: SPACING.lg,
  lineHeight: TYPOGRAPHY.lineHeight.normal,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: SPACING.lg,
  marginTop: SPACING.md,
};

const labelStyle: React.CSSProperties = {
  fontSize: TYPOGRAPHY.fontSize.base,
  fontWeight: TYPOGRAPHY.fontWeight.medium,
  color: COLORS.text.primary,
};

const numberInputStyle: React.CSSProperties = {
  width: '80px',
  padding: `${SPACING.sm} ${SPACING.md}`,
  border: `1px solid ${COLORS.border.medium}`,
  borderRadius: BORDER_RADIUS.md,
  fontSize: TYPOGRAPHY.fontSize.base,
  color: COLORS.text.primary,
  textAlign: 'center',
};

const selectStyle: React.CSSProperties = {
  ...SELECT_CHEVRON_STYLE,
  padding: `${SPACING.sm} 36px ${SPACING.sm} ${SPACING.md}`,
  border: `1px solid ${COLORS.border.medium}`,
  borderRadius: BORDER_RADIUS.md,
  fontSize: TYPOGRAPHY.fontSize.base,
  color: COLORS.text.primary,
  backgroundColor: COLORS.background.primary,
  minWidth: '220px',
};

const feedbackStyle: React.CSSProperties = {
  marginTop: SPACING.lg,
  padding: SPACING.lg,
  borderRadius: BORDER_RADIUS.md,
  fontSize: TYPOGRAPHY.fontSize.sm,
  fontWeight: TYPOGRAPHY.fontWeight.medium,
};

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Dublin', label: 'Europe/Dublin (IST / GMT)' },
  { value: 'Europe/London', label: 'Europe/London (GMT / BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET / CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET / CEST)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (CET / CEST)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (CET / CEST)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET / CEST)' },
  { value: 'Europe/Warsaw', label: 'Europe/Warsaw (CET / CEST)' },
  { value: 'Europe/Bucharest', label: 'Europe/Bucharest (EET / EEST)' },
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki (EET / EEST)' },
  { value: 'Europe/Athens', label: 'Europe/Athens (EET / EEST)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
  { value: 'America/New_York', label: 'America/New_York (EST / EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST / CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST / MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST / PDT)' },
  { value: 'America/Toronto', label: 'America/Toronto (EST / EDT)' },
  { value: 'America/Vancouver', label: 'America/Vancouver (PST / PDT)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT / BRST)' },
  { value: 'America/Mexico_City', label: 'America/Mexico_City (CST / CDT)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul (KST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT)' },
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh (AST)' },
  { value: 'Africa/Lagos', label: 'Africa/Lagos (WAT)' },
  { value: 'Africa/Nairobi', label: 'Africa/Nairobi (EAT)' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (SAST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST / AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST / AEDT)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST / NZDT)' },
  { value: 'UTC', label: 'UTC' },
];
